import React, { useMemo } from 'react';
import { string } from 'prop-types';

import {
    DocumentsListingLoader,
    ListingLoader,
    TradeShowsListingLoader
} from 'components/skeleton-loader';
import RCPagination from 'components/pagination/RCPagination';

import {
    ActiveFilters,
    Filters,
    FiltersTriggerButton,
    LocationsListing,
    MediaRoomListing,
    NewsListing,
    Results,
    Search,
    Sorting,
    SubcategoryBanners,
    TradeShowsListing
} from './components';
import { useListingAPI, useMobileFilters } from './hooks';
import { LISTING_CONTAINER_ID, URL_PARAMETER_PAGE } from './constants';
import { getLocalization } from './utils';

const FilteredListing = ({ configName }) => {
    const config = useMemo(() => {
        if (!window.config || !window.config[configName]) {
            return {};
        }

        return window.config[configName];
    }, []);

    const {
        advancedFiltersTitle,
        api,
        culture,
        title,
        searchField,
        filtersTitle,
        filtersTitleIsHiddenOnMobile,
        listingType,
        numberFormat,
        siteName
    } = config;

    const {
        activeFilters,
        activeFiltersWithSubcategories,
        advancedFilters,
        items,
        filters,
        pagination,
        paginationURLBase,
        nextItemsAmount,
        showPagination,
        searchValue,
        selectedSorting,
        sorting,
        isLoading,
        isLoadingMore,
        isSearchLoaderVisible,
        scrollAnchorRef,
        handleAdvancedFiltersSelectChange,
        handleShowAllClick,
        handlePageChange,
        handleLoadMoreCLick,
        handleSearchInputChange,
        handleSubmit,
        handleCheckboxChange,
        handleResetSelect,
        handleSortingChange,
        handleClearFilters,
        handleRadioChange
    } = useListingAPI({
        api,
        pagination: config.pagination,
        filters: config.filters,
        sorting: config.sorting,
        advancedFilters: config.advancedFilters,
        culture,
        siteName
    });
    const { toggleFilter } = useMobileFilters();

    const showDocumentsListingLoader = useMemo(() => {
        const documentsFilter = filters.find(
            filter => filter.id === 'documents'
        );

        if (!documentsFilter || !documentsFilter.radio) {
            return false;
        }
        return documentsFilter.radio.checked;
    }, [filters]);

    const hasItems = !!items && !!items.length;

    const renderListing = type => {
        switch (type) {
            case 'mediaRoom':
                return (
                    <MediaRoomListing
                        configName={configName}
                        items={items}
                        filters={filters}
                        handleShowAllClick={handleShowAllClick}
                    />
                );

            case 'news':
                return <NewsListing items={items} />;

            case 'locations':
                return <LocationsListing items={items} />;

            case 'tradeShows':
                return <TradeShowsListing items={items} />;

            default:
                return null;
        }
    };

    const renderLoaderByListingType = type => {
        switch (type) {
            case 'mediaRoom':
                {
                    if (showDocumentsListingLoader) {
                        return (
                            <DocumentsListingLoader
                                count={pagination.rowsOnPage}
                            />
                        );
                    }
                }
                return <ListingLoader count={pagination.rowsOnPage} />;

            case 'locations':
                return (
                    <ListingLoader
                        count={pagination.rowsOnPage}
                        perex={false}
                        button={false}
                    />
                );

            case 'news':
                return (
                    <ListingLoader
                        count={pagination.rowsOnPage}
                        perex={false}
                    />
                );

            case 'tradeShows':
                return (
                    <TradeShowsListingLoader count={pagination.rowsOnPage} />
                );

            default:
                return <ListingLoader count={pagination.rowsOnPage} />;
        }
    };

    return (
        <div className="c-listing" id={LISTING_CONTAINER_ID}>
            <div className="wrapper">
                <div className="c-listing__header">
                    <div className="grid">
                        <div className="col-md-4 col-lg-3">
                            {title && (
                                <h1 className="c-listing__title h2">{title}</h1>
                            )}
                        </div>
                        {!!activeFilters && !!activeFilters.length && (
                            <div className="col-md-8 col-lg-9">
                                <ActiveFilters
                                    activeFilters={activeFilters}
                                    clearFiltersButtonTitle={getLocalization(
                                        configName,
                                        'button/clearFilters'
                                    )}
                                    clearFilterTitle={getLocalization(
                                        configName,
                                        'filter/active/clear'
                                    )}
                                    handleClearFilters={handleClearFilters}
                                    onCheckboxChange={handleCheckboxChange}
                                    onResetSelect={handleResetSelect}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="grid">
                    <div className="col-md-4 col-lg-3">
                        <Filters
                            activeFilters={activeFilters}
                            advancedFilters={advancedFilters}
                            advancedFiltersTitle={advancedFiltersTitle}
                            clearFiltersButtonTitle={getLocalization(
                                configName,
                                'button/clearFilters'
                            )}
                            clearFilterTitle={getLocalization(
                                configName,
                                'filter/active/clear'
                            )}
                            closeButtonTitle={getLocalization(
                                configName,
                                'filter/modal/close'
                            )}
                            filters={filters}
                            filtersModalTitle={getLocalization(
                                configName,
                                'filter/modal/title'
                            )}
                            filtersTitle={filtersTitle}
                            filtersTitleIsHiddenOnMobile={
                                filtersTitleIsHiddenOnMobile
                            }
                            handleAdvancedFiltersSelectChange={
                                handleAdvancedFiltersSelectChange
                            }
                            handleClearFilters={handleClearFilters}
                            handleRadioChange={handleRadioChange}
                            isLoading={isLoading || isLoadingMore}
                            labelCollapse={getLocalization(
                                configName,
                                'filter/collapse'
                            )}
                            labelExpand={getLocalization(
                                configName,
                                'filter/expand'
                            )}
                            numberFormat={numberFormat}
                            results={pagination.rowsCount}
                            onCheckboxChange={handleCheckboxChange}
                            onResetSelect={handleResetSelect}
                            onCloseButtonClick={toggleFilter}
                            showResultsButtonTitle={getLocalization(
                                configName,
                                'button/showResults'
                            )}
                        />
                    </div>
                    <div className="col-md-8 col-lg-9">
                        <div className="c-listing__search">
                            <Search
                                isLoading={isLoading || isLoadingMore}
                                isSearchLoaderVisible={isSearchLoaderVisible}
                                searchButtonLabel={getLocalization(
                                    configName,
                                    'search/button'
                                )}
                                searchField={searchField}
                                searchValue={searchValue}
                                handleSubmit={handleSubmit}
                                onSearchInputChange={handleSearchInputChange}
                            />
                        </div>
                        <div className="c-listing__block c-listing__block--v-centered">
                            <Results
                                configName={configName}
                                numberFormat={numberFormat}
                                results={pagination.rowsCount}
                            />
                            {sorting && (
                                <Sorting
                                    disabled={
                                        !hasItems || isLoading || isLoadingMore
                                    }
                                    onChange={handleSortingChange}
                                    selectedSorting={selectedSorting}
                                    sorting={sorting}
                                />
                            )}
                        </div>
                        <SubcategoryBanners
                            activeFilters={activeFiltersWithSubcategories}
                            onCheckboxChange={handleCheckboxChange}
                        />
                        <div ref={scrollAnchorRef} />
                        {isLoading && renderLoaderByListingType(listingType)}
                        {!isLoading && !!items && !!items.length && (
                            <React.Fragment>
                                {renderListing(listingType)}
                            </React.Fragment>
                        )}
                        {!isLoading && (!items || !items.length) && (
                            <p className="c-listing__no-results-text">
                                {getLocalization(
                                    configName,
                                    'listing/noResults'
                                )}
                            </p>
                        )}
                        {showPagination && (
                            <div className="c-listing__pagination">
                                <RCPagination
                                    current={pagination.currentPage}
                                    isLoading={isLoadingMore || isLoading}
                                    localization={{
                                        loadMore: getLocalization(
                                            configName,
                                            'pagination/loadMore'
                                        ),
                                        ariaLabel: getLocalization(
                                            configName,
                                            'pagination/ariaLabel'
                                        ),
                                        next: getLocalization(
                                            configName,
                                            'pagination/next'
                                        ),
                                        previous: getLocalization(
                                            configName,
                                            'pagination/previous'
                                        )
                                    }}
                                    nextItemsAmount={nextItemsAmount}
                                    onChange={handlePageChange}
                                    onLoadMoreCLick={handleLoadMoreCLick}
                                    pageSize={pagination.rowsOnPage}
                                    pageURLParameter={URL_PARAMETER_PAGE}
                                    total={pagination.rowsCount}
                                    urlBase={paginationURLBase}
                                    withLoadMore
                                />
                            </div>
                        )}
                    </div>
                </div>
                <FiltersTriggerButton
                    disabled={isLoading || isLoadingMore}
                    onClick={toggleFilter}
                    title={getLocalization(configName, 'filter/triggerButton')}
                />
            </div>
        </div>
    );
};

FilteredListing.propTypes = {
    configName: string.isRequired
};

export default FilteredListing;
